<template>
  <div class="max-w-2xl w-full mx-auto">

    <!-- Error detected with file upload -->
    <div v-if="isFileUploadModalVisible" class="fixed inset-0 bg-red-500 bg-opacity-75 flex items-center justify-center z-50">
      <div class="bg-white p-8 rounded-lg shadow-lg w-96">
        <div class="flex items-center">
          <span class="text-3xl pr-4">⚠️</span>
          <h2 class="text-xl font-bold text-red-600">Warning: We've detected an issue</h2>
        </div>
        <p class="mt-4 text-lg">{{ fileUploadMessage }}</p>
        <div class="mt-6 flex justify-between">
          <button @click="acceptError(setSourceFileToNull)" class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600">
            Accept error
          </button>
        </div>
      </div>
    </div>

    <!-- Show All ceremonies warning modal -->
    <div v-if="showAll && isWarningModalVisible" class="fixed inset-0 bg-red-500 bg-opacity-75 flex items-center justify-center z-50">
      <div class="bg-white p-8 rounded-lg shadow-lg w-96">
        <div class="flex items-center">
          <span class="text-3xl pr-4">⚠️</span>
          <h2 class="text-xl font-bold text-red-600">Warning: Potentially Dangerous Action</h2>
        </div>
        <p class="mt-4 text-lg">You are about view ceremonies that have already been uploaded. Please carefully select the ceremony you wish to upload and proceed with <b>caution</b> or cancel.</p>
        <div class="mt-6 flex justify-between">
          <button @click="showAllCeremonies(false)" class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600">
            Cancel
          </button>
          <button @click="closeWarningModal" class="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700">
            Proceed
          </button>
        </div>
      </div>
    </div>

    <!-- Show All ceremony overwrite modal -->
    <div v-if="isCeremonyOverwriteModalVisible" class="fixed inset-0 bg-red-500 bg-opacity-75 flex items-center justify-center z-50">
      <div class="bg-white p-8 rounded-lg shadow-lg md:w-2/4 w-96 ">
        <div class="flex items-center">
          <span class="text-3xl pr-4">⚠️</span>
          <h2 class="md:text-2xl text-xl font-bold text-red-600">Warning: Potentially Dangerous Action</h2>
        </div>
        <p class="mt-4 text-lg">You are about to upload footage of a ceremony that may already have existing footage. To continue, 
          please verify that both the ceremony and the file are correct, then enter 
          <span style="user-select: none;">'Permanently Overwrite'</span> and click Overwrite. If you wish to cancel, select Cancel.
        </p>
        <p class="mt-4 text-md"> Ceremony:
          <b>
            {{ formatCeremonyOption(ceremonies.find(x => x.id == selected_ceremony) || null) }}
          </b>
        </p>
        <p class="my-2 text-md">Source: <b>{{ selectedSourceLabel }}</b></p>
        <div class="flex flex-col">
          <input 
            type="text" 
            class="p-2 my-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500" 
            placeholder="Permanently Overwrite"
            v-model="validateString"
          >
        </div>
        <div class="mt-6 flex justify-between">
          <button @click="isCeremonyOverwriteModalVisible = false" class="bg-gray-500 text-white py-2 px-4 rounded hover:bg-gray-600">
            Cancel
          </button>
          <button 
            id="overwriteModalSubmitBtn"
            @click="successOverwriteValidation" 
            :disabled="!isStringOverwriteValid"
            class="bg-red-600 text-white py-2 px-4 rounded hover:bg-red-700 disabled:opacity-75"
          >
            Overwrite
          </button>
        </div>
      </div>
    </div>

    <div v-if="completed" class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
      <div class="mt-16 mx-auto max-w-7xl px-4 sm:mt-24 sm:px-6">
        <div class="text-center">
          <h1
            class="text-4xl tracking-tight font-extrabold text-gray-900 sm:text-5xl md:text-6xl"
          >
            <span class="block">{{ source_mode === 'file' ? 'Upload Completed' : 'Source Input Submitted' }}</span>
          </h1>
          <p
            class="mt-3 max-w-md mx-auto text-base text-gray-700 sm:text-lg md:mt-5 md:text-xl md:max-w-3xl"
          >
            {{ source_mode === 'file' ? 'You uploaded source footage for' : 'You submitted a source input for' }} {{ selected_university["name"] }}
            {{ formatCeremonyOption(ceremonies.find(x => x.id == selected_ceremony) || null) }}
          </p>
          <div v-if="canManageSourceInputs && source_input_jobs.length" class="mt-6 text-left">
            <div
              v-for="job in source_input_jobs.slice(0, 3)"
              :key="job.id"
              class="border-b border-gray-200 py-2 text-sm last:border-b-0"
            >
              <div class="flex flex-wrap items-center justify-between gap-2">
                <div class="font-medium text-gray-900">{{ formatJobType(job.job_type) }}</div>
                <span :class="jobStatusClass(job.status)">{{ formatJobStatus(job.status) }}</span>
              </div>
              <div class="mt-1 text-gray-700">{{ formatJobSchedule(job) }}</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <form class="space-y-4 text-gray-700" @submit.prevent v-if="!completed">
      <fieldset :disabled="isUploading" class="space-y-4">
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="mt-8 mx-auto max-w-7xl px-4 sm:mt-4 sm:px-6">
          <div class="text-center">
            <h1
              class="text-2xl tracking-tight font-extrabold text-gray-900 sm:text-2xl md:text-2xl"
            >
              <span class="block text-mygrad">Add Ceremony Footage</span>
            </h1>
          </div>
        </div>
      </div>
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full px-2">
          <button
            v-if="!showAll"
            type="submit"
            @click="showAllCeremonies(true)"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Show All Ceremonies (warning this will include ceremonies you have
            already uploaded)
          </button>
          <button
            v-else
            type="submit"
            @click="showAllCeremonies(false)"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Hide already uploaded ceremonies (Normal mode)
          </button>
        </div>
      </div>
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full px-2">
          <h2 v-if="stage >= 0 && universities.length > 0">
            Select University
          </h2>
          <select
            class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
            v-model="selected_university"
            @change="onUniChange"
            v-if="stage >= 0 && universities.length > 0"
          >
            <option disabled :value="null">Select University</option>
            <option
              v-for="(uni, c_index) in this.universities"
              :key="c_index"
              :value="uni"
            >
              {{ uni["name"] }}
            </option>
          </select>
        </div>
      </div>
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full px-2">
          <h2 v-if="stage >= 1">Now Select Ceremony</h2>
          <select
            v-if="stage >= 1"
            v-model="selected_ceremony"
            @change="onCeremonyChange"
            class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
          >
            <option disabled :value="null">Select your event date</option>

            <option
              v-for="ceremony in ceremonies"
              :value="ceremony['id']"
              :key="ceremony['id']"
            >
              {{ formatCeremonyOption(ceremony) }}
            </option>
          </select>
        </div>
      </div>
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full px-2">
          <h2 v-if="stage >= 1">Use this source for</h2>
          <div v-if="stage >= 1" class="mt-2 grid grid-cols-1 gap-2 text-sm">
            <label class="flex items-center gap-2 rounded border border-gray-300 p-2">
              <input type="checkbox" value="clips" v-model="source_usages" checked/>
              <span>Personalised clips</span>
            </label>
            <label class="flex items-center gap-2 rounded border border-gray-300 p-2">
              <input type="checkbox" value="digital_download" v-model="source_usages" />
              <span>Full ceremony digital download</span>
            </label>
          </div>
        </div>
      </div>
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full px-2">
          <h2 v-if="selected_ceremony != null">Choose how footage will arrive</h2>
          <div
            v-if="selected_ceremony != null"
            class="mt-2 grid grid-cols-1 gap-2"
            :class="canManageSourceInputs ? 'sm:grid-cols-3' : ''"
          >
            <button
              type="button"
              @click="setSourceMode('file')"
              :class="sourceModeButtonClass('file')"
            >
              <span class="font-semibold">Upload a file</span>
              <span class="mt-1 text-xs font-normal">Use an MP4 from this computer.</span>
            </button>
            <button
              v-if="canManageSourceInputs"
              type="button"
              @click="setSourceMode('external_url')"
              :class="sourceModeButtonClass('external_url')"
            >
              <span class="font-semibold">Record live URL</span>
              <span class="mt-1 text-xs font-normal">Use a live HLS/M3U8, RTMP/SRT, or supported live event URL.</span>
            </button>
            <button
              v-if="canManageSourceInputs"
              type="button"
              @click="setSourceMode('stream')"
              :class="sourceModeButtonClass('stream')"
            >
              <span class="font-semibold">Create a live ingest</span>
              <span class="mt-1 text-xs font-normal">Generate our RTMPS/SRT details and stream key for this ceremony.</span>
            </button>
          </div>
          <div
            v-if="canManageSourceInputs && (current_input_settings || source_input_jobs.length)"
            class="mt-3 rounded border border-gray-300 bg-gray-50 p-3 text-sm text-gray-800"
          >
            <div class="flex flex-wrap items-center justify-between gap-2">
              <div>
                <div class="font-semibold">Current source setup</div>
                <div>{{ currentSourceSetupLabel }}</div>
              </div>
              <span v-if="currentSourceStatusLabel" :class="currentSourceStatusClass">
                {{ currentSourceStatusLabel }}
              </span>
            </div>
            <div v-if="currentSourceDetailLines.length" class="mt-2 space-y-1 text-gray-700">
              <div v-for="line in currentSourceDetailLines" :key="line">{{ line }}</div>
            </div>
            <div class="mt-2">
              <router-link
                class="inline-flex items-center px-2 py-1 border border-gray-300 rounded bg-white hover:bg-gray-50"
                :to="{ path: '/admin/source-inputs', query: { universityId: selected_university && selected_university.id } }"
              >
                Open Source Inputs
              </router-link>
            </div>
            <div v-if="latestSourceInputJob" class="mt-2 border-t border-gray-200 pt-2">
              <div class="flex flex-wrap items-center justify-between gap-2">
                <div>{{ formatJobType(latestSourceInputJob.job_type) }}</div>
                <span :class="jobStatusClass(latestSourceInputJob.status)">
                  {{ formatJobStatus(latestSourceInputJob.status) }}
                </span>
              </div>
              <div class="mt-1 text-gray-700">{{ formatJobSchedule(latestSourceInputJob) }}</div>
              <div v-if="latestSourceInputJob.error_message" class="mt-1 text-red-700 break-words">
                {{ latestSourceInputJob.error_message }}
              </div>
              <button
                v-if="isAvailableIvsRecordingSession(latestSourceInputJob)"
                type="button"
                @click="selectIvsRecordingSession(latestSourceInputJob)"
                :disabled="isUploading"
                class="mt-2 inline-flex items-center px-3 py-2 border border-indigo-600 shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-75"
              >
                Use as ceremony footage
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0" v-if="canManageSourceInputs && selected_ceremony != null && source_mode === 'external_url'">
        <div class="w-full px-2 space-y-2">
          <h2>Record a live source URL</h2>
          <p class="text-sm text-gray-700">
            Use this when another platform is already hosting the live stream.
          </p>
          <input
            class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
            type="url"
            v-model.trim="external_source_url"
            placeholder="https://.../live.m3u8 or a Vimeo Live event URL"
          />
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label class="text-sm sm:col-span-2">
              Schedule timezone
              <input class="w-full h-10 px-3 text-base border rounded-lg" list="source-input-timezones" v-model.trim="schedule_timezone" />
            </label>
            <label class="text-sm">
              Recording start
              <input class="w-full h-10 px-3 text-base border rounded-lg" type="datetime-local" v-model="scheduled_start_at" />
            </label>
            <label class="text-sm">
              Recording end
              <input class="w-full h-10 px-3 text-base border rounded-lg" type="datetime-local" v-model="scheduled_end_at" />
            </label>
            <label class="text-sm">
              Reconnect grace seconds
              <input class="w-full h-10 px-3 text-base border rounded-lg" type="number" min="1" max="1800" v-model.number="reconnect_grace_seconds" />
            </label>
            <label class="text-sm">
              Stall timeout seconds
              <input class="w-full h-10 px-3 text-base border rounded-lg" type="number" min="1" max="300" v-model.number="stall_timeout_seconds" />
            </label>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0" v-if="canManageSourceInputs && selected_ceremony != null && source_mode === 'stream'">
        <div class="w-full px-2 space-y-2">
          <h2>Create a live ingest</h2>
          <p class="text-sm text-gray-700">
            Use this when the AV team will stream directly into MyGraduationClip. Saving creates the ceremony ingest and returns the encoder URL and stream key.
          </p>
          <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label class="text-sm sm:col-span-2">
              Schedule timezone
              <input class="w-full h-10 px-3 text-base border rounded-lg" list="source-input-timezones" v-model.trim="schedule_timezone" />
            </label>
            <label class="text-sm">
              Recording start
              <input class="w-full h-10 px-3 text-base border rounded-lg" type="datetime-local" v-model="scheduled_start_at" />
            </label>
            <label class="text-sm">
              Recording end
              <input class="w-full h-10 px-3 text-base border rounded-lg" type="datetime-local" v-model="scheduled_end_at" />
            </label>
            <label class="text-sm">
              Reconnect grace seconds
              <input class="w-full h-10 px-3 text-base border rounded-lg" type="number" min="1" max="1800" v-model.number="reconnect_grace_seconds" />
            </label>
            <label class="text-sm">
              Stall timeout seconds
              <input class="w-full h-10 px-3 text-base border rounded-lg" type="number" min="1" max="300" v-model.number="stall_timeout_seconds" />
            </label>
          </div>
          <div class="flex flex-wrap gap-2">
            <label class="inline-flex items-center gap-2 text-sm border border-gray-300 rounded p-2">
              <input type="checkbox" value="rtmp" v-model="stream_protocols" />
              <span>RTMPS</span>
            </label>
            <label class="inline-flex items-center gap-2 text-sm border border-gray-300 rounded p-2">
              <input type="checkbox" value="srt" v-model="stream_protocols" />
              <span>SRT</span>
            </label>
          </div>
          <div class="flex flex-wrap gap-2 items-center">
            <input
              type="button"
              value="Save stream settings"
              @click="saveStreamSettings()"
              :disabled="!canSaveStreamSettings || isUploading"
              class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-75"
            />
            <input
              type="button"
              value="Rotate stream key"
              @click="rotateStreamKey()"
              :disabled="!canRotateStreamKey || isUploading"
              class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-75"
            />
            <span v-if="stream_key_hint" class="text-sm text-gray-700">Current key ends {{ stream_key_hint }}</span>
          </div>
          <div v-if="new_stream_key" class="rounded border border-yellow-300 bg-yellow-50 p-3 text-sm break-all">
            <div class="font-semibold">Stream key - copy now</div>
            <div>{{ new_stream_key }}</div>
          </div>
          <div v-if="Object.keys(stream_ingest_urls).length" class="rounded border border-gray-300 bg-gray-50 p-3 text-sm break-all">
            <div class="font-semibold">Encoder ingest URLs</div>
            <div v-for="(url, protocol) in stream_ingest_urls" :key="protocol">
              <span class="uppercase">{{ protocol }}</span>: {{ url }}
            </div>
            <div v-if="!new_stream_key && stream_key_hint" class="mt-2 text-xs text-gray-700">
              Full stream keys are shown only when first created or rotated.
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0" v-if="canManageSourceInputs && selected_ceremony != null && source_input_jobs.length">
        <div class="w-full px-2 space-y-2">
          <h2>Recent source input jobs</h2>
          <div class="overflow-hidden rounded border border-gray-300">
            <div
              v-for="job in source_input_jobs"
              :key="job.id"
              class="border-b border-gray-200 p-3 text-sm last:border-b-0"
            >
              <div class="flex flex-wrap items-center justify-between gap-2">
                <div class="font-medium text-gray-900">{{ formatJobType(job.job_type) }}</div>
                <span :class="jobStatusClass(job.status)">{{ formatJobStatus(job.status) }}</span>
              </div>
              <div class="mt-1 text-gray-700">{{ formatJobSchedule(job) }}</div>
              <div v-if="job.metadata && job.metadata.recording_duration_ms" class="mt-1 text-gray-700">
                Duration: {{ formatDurationMs(job.metadata.recording_duration_ms) }}
              </div>
              <div v-if="job.error_message" class="mt-1 text-red-700 break-words">{{ job.error_message }}</div>
              <button
                v-if="isAvailableIvsRecordingSession(job)"
                type="button"
                @click="selectIvsRecordingSession(job)"
                :disabled="isUploading"
                class="mt-2 inline-flex items-center px-3 py-2 border border-indigo-600 shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-75"
              >
                Use as ceremony footage
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0" v-if="source_mode === 'file'">
        <div class="w-full px-2">
          <input
            class="w-full h-10 px-3 text-base placeholder-gray-600 border rounded-lg focus:shadow-outline"
            type="file"
            name="file"
            ref="file"
            accept="video/mp4"
            @change="onFileChange"
            v-if="selected_ceremony != null"
          />
        </div>
      </div>

      <div
        v-if="source_file && selected_ceremony && source_mode === 'file' && !sourceUploadLimitExceeded"
        class="px-2"
      >
        <recording-duplicate-check
          ref="duplicateCheck"
          :file="source_file"
          :ceremony-id="selected_ceremony"
          :destination="`${selected_university.name} · ${formatCeremonyOption(ceremonies.find(c => c.id === selected_ceremony))}`"
          :format-ceremony="formatCeremonyOption"
          @checking="duplicateCheckPending = $event"
          @replace="replaceSourceFile"
        />
      </div>

      <div
        v-if="ceremonySkimEnabled && source_file && source_mode === 'file'"
        class="flex flex-wrap -mx-2 space-y-4 md:space-y-0"
      >
        <div class="w-full px-2">
          <ceremony-skim-assist
            :skim="ceremonySkim"
            :clip-window="clipWindow"
            @select-boundary="selectCeremonySkimBoundary"
            @seek="seekCeremonySkimFrame"
          />
        </div>
      </div>

      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full min-w-0 overflow-hidden px-2">
          <video-player
            v-if="source_file"
            class="upload-video-player"
            :options="videoOptions"
            ref="videoPlayer"
          ></video-player>
        </div>
      </div>

      <div
        class="flex flex-wrap -mx-2 space-y-4 md:space-y-0"
        v-if="clipProcessingRequested && source_file != null && source_mode === 'file'"
      >
        <div class="w-full px-2">
          <h3>Clip window</h3>
          <div class="mt-2 flex flex-wrap gap-2 text-sm">
            <button
              type="button"
              @click="markClipWindow('start')"
              class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Mark clip start
            </button>
            <button
              type="button"
              @click="markClipWindow('end')"
              class="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              Mark clip end
            </button>
            <span v-if="clipWindow.startSeconds !== null" class="px-2 py-2 rounded bg-gray-100">
              Start: {{ formatSeconds(clipWindow.startSeconds) }}
            </span>
            <span v-if="clipWindow.endSeconds !== null" class="px-2 py-2 rounded bg-gray-100">
              End: {{ formatSeconds(clipWindow.endSeconds) }}
            </span>
          </div>
          <p
            v-if="clipWindowError"
            class="mt-2 text-sm text-red-600"
          >
            {{ clipWindowError }}
          </p>
          <p
            v-if="sourceUploadLimitExceeded"
            class="mt-2 text-sm text-red-600"
          >
            This file is over the current 6GB source upload limit. Please upload a smaller source file.
          </p>
        </div>
      </div>

      <div
        class="flex flex-wrap -mx-2 space-y-4 md:space-y-0"
        v-if="clipProcessingRequested && source_file != null && source_mode === 'file'"
      >
        <div class="w-full px-2">
          <h3>
            Now please go to a reference frame for the first walk shot, press
            pause and then press Create First Reference
          </h3>
          <button
            type="submit"
            @click="createReference('wide')"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Create First Reference
          </button>
        </div>
      </div>

      <div
        class="flex flex-wrap -mx-2 space-y-4 md:space-y-0"
        v-if="clipProcessingRequested && references.wide != null && source_mode === 'file'"
      >
        <div class="w-full px-2">
          <h3>
            Now please go to a reference frame for the SECOND walk shot, press
            pause and then press Create SECOND Reference
          </h3>
          <button
            type="submit"
            @click="createReference('close')"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Create Second Reference
          </button>
        </div>
      </div>
      <div class="flex flex-wrap -mx-2 space-y-4 md:space-y-0">
        <div class="w-full px-2">
          <datalist id="source-input-timezones">
            <option v-for="timezone in availableScheduleTimezones" :key="timezone" :value="timezone"></option>
          </datalist>
          <button
            type="submit"
            @click="checkForOverwrite"
            class="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            v-if="canSubmitUpload"
            :disabled="isUploading || (source_mode === 'file' && duplicateCheckPending)"
          >
            {{ submitActionLabel }}
          </button>
          <div
            class="w-full bg-gray-200 rounded-full dark:bg-gray-700"
            v-if="stage > 50 || stage == -1"
          >
            <div
              class="bg-blue-600 text-xs font-medium text-blue-100 text-center p-0.5 leading-none rounded-full"
              :style="'width: ' + progress + '%'"
            >
              {{ progress }}%
            </div>
          </div>
          <p v-if="uploadStatus" class="mt-2 text-sm text-gray-700">
            {{ uploadStatus }}
          </p>
        </div>
      </div>
      </fieldset>
    </form>
  </div>
</template>


<script>
import axios from "axios";
import "../../plugins/video-player";
import { trackAdminEvent } from '../../admin/activity.js'
import { getAccessContext, hasToolAccess } from '../../admin/access.js'
import { buildCeremonySkimInput } from '../../admin/ceremonySkim.js'
import CeremonySkimAssist from './CeremonySkimAssist.vue'
import RecordingDuplicateCheck from './RecordingDuplicateCheck.vue'

const MAX_PARALLEL_PART_UPLOADS = 3;
const MAX_PART_UPLOAD_ATTEMPTS = 3;
const PART_UPLOAD_RETRY_BASE_DELAY_MS = 1000;

function emptyCeremonySkimState() {
  return {
    status: 'idle',
    progress: 0,
    message: '',
    localOverviewMs: null,
    totalMs: null,
    frames: [],
    analysis: null,
    runId: null,
    error: '',
  }
}

export default {
  name: "Upload",
  data: function () {
    return {
      stage: 0,
      isUploading: false,
      progress: false,
      universities: [],
      selected_university: null,
      ceremonies: [],
      selected_ceremony: null,
      source_mode: "file",
      source_file: null,
      duplicateCheckPending: false,
      duplicateCheckPayload: null,
      external_source_url: "",
      external_job_type: "live_url",
      scheduled_start_at: "",
      scheduled_end_at: "",
      schedule_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC",
      stream_protocols: ["rtmp", "srt"],
      stream_ingest_urls: {},
      reconnect_grace_seconds: 300,
      stall_timeout_seconds: 45,
      stream_key_hint: "",
      new_stream_key: "",
      source_input_jobs: [],
      current_input_settings: null,
      source_usages: ["clips"],
      references: {
        wide: null,
        close: null,
      },
      clipWindow: {
        startSeconds: null,
        endSeconds: null,
      },
      completed: false,
      showAll: false,
      isWarningModalVisible: true,
      isCeremonyOverwriteModalVisible: false,
      validateString: '',
      fileUploadMessage: '',
      isFileUploadModalVisible: false,
      setSourceFileToNull: false,
      clipWindowError: '',
      uploadStatus: '',
      sourceInputPollTimer: null,
      sourceInputPollInFlight: false,
      ceremonySkim: emptyCeremonySkimState(),
      ceremonySkimRunToken: 0,
    };
  },
  computed: {
    ceremonySkimEnabled() {
      return process.env.VUE_APP_CEREMONY_SKIM_ENABLED === 'true'
    },
    canManageSourceInputs() {
      return hasToolAccess('sourceInputs', getAccessContext())
    },
    videoOptions() {
      var URL = window.URL || window.webkitURL;
      var fileURL = URL.createObjectURL(this.source_file);

      return {
        sources: [
          {
            type: "video/mp4",
            src: fileURL,
          },
        ],
        aspectRatio: "16:9",
        fluid: true,
        responsive: true,
      };
    },
    player() {
      return this.$refs.videoPlayer.player;
    },
    isStringOverwriteValid() {
      return this.validateString === 'Permanently Overwrite'
    },
    clipProcessingRequested() {
      return this.source_usages.includes("clips")
    },
    selectedSourceLabel() {
      if (this.source_mode === "file") return this.source_file ? this.source_file.name : "No file selected"
      if (this.source_mode === "external_url") return this.external_source_url || "External URL"
      return this.stream_key_hint ? `Live stream key ending ${this.stream_key_hint}` : "Live stream"
    },
    hasValidScheduleWindow() {
      if (!this.scheduled_start_at || !this.scheduled_end_at) return false
      const startIso = this.scheduleDateTimeToIso(this.scheduled_start_at)
      const endIso = this.scheduleDateTimeToIso(this.scheduled_end_at)
      if (!startIso || !endIso) return false
      const start = new Date(startIso)
      const end = new Date(endIso)
      if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return false
      return end > start
    },
    availableScheduleTimezones() {
      const current = this.schedule_timezone || "UTC"
      const fallback = ["UTC", "Europe/London", "Europe/Dublin"]
      const supported = typeof Intl.supportedValuesOf === "function"
        ? Intl.supportedValuesOf("timeZone")
        : fallback
      return Array.from(new Set([current, ...fallback, ...supported])).filter(Boolean).sort()
    },
    canSaveStreamSettings() {
      return this.canManageSourceInputs && this.selected_ceremony != null && this.stream_protocols.length > 0 && this.hasValidScheduleWindow
    },
    canRotateStreamKey() {
      return this.selected_ceremony != null && this.stream_protocols.length > 0 && (this.stream_key_hint || this.canSaveStreamSettings)
    },
    submitActionLabel() {
      if (this.source_mode === "file") return "Upload file"
      if (this.source_mode === "external_url") return "Schedule URL recording"
      return "Create live ingest"
    },
    canSubmitUpload() {
      if (!this.source_usages.length) return false
      if (this.source_mode !== "file" && !this.canManageSourceInputs) return false
      if (this.source_mode === "external_url") {
        if (!this.external_source_url) return false
        return this.hasValidScheduleWindow
      }
      if (this.source_mode === "stream") {
        return this.canSaveStreamSettings
      }
      if (!this.source_file) return false
      if (this.sourceUploadLimitExceeded) return false
      if (this.clipWindowError) return false
      if (
        (this.clipWindow.startSeconds !== null || this.clipWindow.endSeconds !== null) &&
        this.clipWindowPayload === null
      ) return false
      if (!this.clipProcessingRequested) return true
      return this.references.wide != null && this.references.close != null
    },
    sourceUploadLimitExceeded() {
      return this.source_file && this.source_file.size > 6 * 1024 * 1024 * 1024
    },
    clipWindowPayload() {
      if (this.clipWindow.startSeconds === null && this.clipWindow.endSeconds === null) return null
      if (this.clipWindow.startSeconds === null || this.clipWindow.endSeconds === null) return null
      if (this.clipWindow.endSeconds <= this.clipWindow.startSeconds) return null
      return {
        startSeconds: Math.floor(this.clipWindow.startSeconds),
        endSeconds: Math.floor(this.clipWindow.endSeconds),
      }
    },
    latestSourceInputJob() {
      return this.source_input_jobs.length ? this.source_input_jobs[0] : null
    },
    currentSourceSetupLabel() {
      const settings = this.current_input_settings
      if (!settings) return "No source input is configured yet"
      if (settings.input_mode === "stream") return "Stream to MyGraduationClip"
      if (settings.input_mode === "external_url") {
        return "External live URL recording"
      }
      return "File upload"
    },
    currentSourceStatusLabel() {
      if (this.latestSourceInputJob) return this.formatJobStatus(this.latestSourceInputJob.status)
      const settings = this.current_input_settings
      if (!settings) return ""
      if (settings.input_mode === "stream" && settings.stream_key_present) return "Ready for encoder"
      if (settings.input_mode === "external_url" && settings.source_url_present) return "Configured"
      return "Configured"
    },
    currentSourceStatusClass() {
      if (this.latestSourceInputJob) return this.jobStatusClass(this.latestSourceInputJob.status)
      const base = "inline-flex items-center rounded px-2 py-1 text-xs font-medium"
      return `${base} bg-green-100 text-green-800`
    },
    currentSourceDetailLines() {
      const settings = this.current_input_settings
      if (!settings) return []
      const lines = []
      if (settings.input_mode === "stream") {
        lines.push(`Provider: ${settings.stream_provider === "ivs" ? "Amazon IVS" : settings.stream_provider || "Stream"}`)
        if (settings.stream_key_hint) lines.push(`Stream key ends ${settings.stream_key_hint}`)
        if (settings.stream_protocols && settings.stream_protocols.length) {
          lines.push(`Protocols: ${settings.stream_protocols.map(protocol => protocol.toUpperCase()).join(", ")}`)
        }
      } else if (settings.input_mode === "external_url") {
        lines.push(`URL type: ${this.formatJobType(settings.source_url_type || "live_url")}`)
        if (settings.source_url_present) lines.push("A source URL is saved for this ceremony")
      }
      if (settings.scheduled_start_at || settings.scheduled_end_at) {
        lines.push(`Schedule: ${this.formatJobSchedule(settings)}`)
      }
      if (settings.schedule_timezone) {
        lines.push(`Timezone: ${settings.schedule_timezone}`)
      }
      return lines
    }
  },
  created() {
    axios
      .get(`/universities`)
      .then((response) => response.data)
      .then(async (response_data) => {
        this.universities = response_data
        await this.applyRouteSelection()
      })
      .catch(() => this.$router.push("/admin/login"));
  },
  mounted() {
    var showAll = this.$route.query.showAll;
    this.showAll = showAll == "true";
  },
  beforeDestroy() {
    this.stopSourceInputPolling()
    this.resetCeremonySkim()
  },
  methods: {
    resetCeremonySkim() {
      this.ceremonySkimRunToken += 1
      this.ceremonySkim = emptyCeremonySkimState()
    },
    ceremonySkimEndpoint() {
      const universityId = this.selected_university && this.selected_university.id
      const base = (process.env.VUE_APP_SOURCE_INPUT_API_BASE_URL || '/source-input').replace(/\/$/, '')
      return `${base}/universities/${universityId}/ceremonies/${this.selected_ceremony}/skim`
    },
    async runCeremonySkim(file) {
      if (!this.ceremonySkimEnabled || !file || !this.selected_ceremony) return
      const runToken = this.ceremonySkimRunToken + 1
      this.ceremonySkimRunToken = runToken
      const started = performance.now()
      this.ceremonySkim = {
        ...emptyCeremonySkimState(),
        status: 'sampling',
        progress: 1,
        message: 'Preparing deterministic local samples',
      }

      try {
        const input = await buildCeremonySkimInput(file, {
          onProgress: update => {
            if (runToken !== this.ceremonySkimRunToken) return
            this.ceremonySkim = {
              ...this.ceremonySkim,
              status: 'sampling',
              progress: update.progress,
              message: update.message,
            }
          },
        })
        if (runToken !== this.ceremonySkimRunToken || file !== this.source_file) return

        this.ceremonySkim = {
          ...this.ceremonySkim,
          status: 'analyzing',
          progress: 82,
          message: 'Checking presentation boundaries',
          localOverviewMs: input.localOverviewMs,
          frames: input.previewFrames,
        }
        const result = await axios.post(this.ceremonySkimEndpoint(), {
          source: input.source,
          sheets: input.sheets.map(sheet => ({
            dataUrl: sheet.dataUrl,
            timestamps: sheet.timestamps,
          })),
        })
        if (runToken !== this.ceremonySkimRunToken || file !== this.source_file) return

        const analysis = result.data && result.data.analysis
        const status = analysis && analysis.status === 'suggested'
          ? 'ready'
          : 'not-visible'
        this.ceremonySkim = {
          ...this.ceremonySkim,
          status,
          progress: 100,
          message: status === 'ready'
            ? 'Suggested presentation window ready'
            : 'Presentation window not established',
          totalMs: Math.round(performance.now() - started),
          analysis,
          runId: result.data && result.data.runId,
        }
        trackAdminEvent('admin_action_completed', {
          toolKey: 'clip_upload',
          metadata: {
            action: 'ceremony_skim_complete',
            ceremonyId: this.selected_ceremony,
            universityId: this.selected_university && this.selected_university.id,
            status,
            runId: result.data && result.data.runId,
            localOverviewMs: input.localOverviewMs,
            totalMs: this.ceremonySkim.totalMs,
            cameraCount: analysis && analysis.estimated_camera_angle_count,
          },
        })
      } catch (error) {
        if (runToken !== this.ceremonySkimRunToken) return
        const apiError = error.response && error.response.data && error.response.data.error
        this.ceremonySkim = {
          ...this.ceremonySkim,
          status: 'failed',
          progress: 100,
          totalMs: Math.round(performance.now() - started),
          error: apiError || error.message || 'Ceremony Skim failed.',
        }
      }
    },
    selectCeremonySkimBoundary({ edge, seconds }) {
      if (!['start', 'end'].includes(edge) || !Number.isFinite(Number(seconds))) return
      const selectedSeconds = edge === 'start'
        ? Math.max(0, Math.floor(seconds))
        : Math.ceil(seconds)
      this.clipWindow[`${edge}Seconds`] = selectedSeconds
      if (
        this.clipWindow.startSeconds !== null
        && this.clipWindow.endSeconds !== null
        && this.clipWindow.endSeconds <= this.clipWindow.startSeconds
      ) {
        this.clipWindowError = 'Clip end must be after clip start.'
      } else {
        this.clipWindowError = ''
      }
      trackAdminEvent('admin_action_completed', {
        toolKey: 'clip_upload',
        metadata: {
          action: 'ceremony_skim_boundary_selected',
          ceremonyId: this.selected_ceremony,
          universityId: this.selected_university && this.selected_university.id,
          edge,
          seconds: selectedSeconds,
          runId: this.ceremonySkim.runId,
        },
      })
    },
    seekCeremonySkimFrame(seconds) {
      if (!this.player || !Number.isFinite(Number(seconds))) return
      this.player.currentTime(seconds)
      this.player.pause()
      const playerElement = this.$refs.videoPlayer && this.$refs.videoPlayer.$el
      if (playerElement && typeof playerElement.scrollIntoView === 'function') {
        playerElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    },
    validateCeremonyOverwrite() {
      this.validateString = ''
      this.isCeremonyOverwriteModalVisible = true
    },
    closeWarningModal() {
      this.isWarningModalVisible = false;
    },
    showAllCeremonies(e) {
      this.$router.push({ path: "/admin/upload", query: { showAll: e } });
      this.$router.go();
    },
    sourceModeButtonClass(mode) {
      const base = "inline-flex min-h-24 flex-col items-start justify-start px-4 py-3 border shadow-sm text-sm font-medium rounded-md text-left focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      return this.source_mode === mode
        ? `${base} border-indigo-600 text-white bg-indigo-600`
        : `${base} border-gray-300 text-gray-700 bg-white hover:bg-gray-50`
    },
    setSourceMode(mode) {
      if (mode !== 'file' && !this.canManageSourceInputs) return
      this.source_mode = mode
      this.uploadStatus = ''
      this.completed = false
      if (mode !== 'file') {
        this.resetCeremonySkim()
        this.source_file = null
        this.references = { wide: null, close: null }
        this.clipWindow = { startSeconds: null, endSeconds: null }
        this.clipWindowError = ''
      }
    },
    createReference(t) {
      // Force user to scroll to a point in the video
      if (this.player.currentTime() === 0) {
        this.fileUploadMessage = "Please set an appropriate reference point.";
        this.setSourceFileToNull = false;
        this.isFileUploadModalVisible = true;
        return;
      }

      if (t == "wide") {
        // TODO: tidy reference
        this.references.wide = this.player.currentTime();
      } else {
        this.references.close = this.player.currentTime();
      }
    },
    markClipWindow(edge) {
      if (!this.player || this.player.currentTime() === undefined) return
      const seconds = Math.floor(this.player.currentTime())
      if (edge === 'start') {
        this.clipWindow.startSeconds = seconds
      } else {
        this.clipWindow.endSeconds = seconds
      }
      if (
        this.clipWindow.startSeconds !== null &&
        this.clipWindow.endSeconds !== null &&
        this.clipWindow.endSeconds <= this.clipWindow.startSeconds
      ) {
        this.clipWindowError = 'Clip end must be after clip start.'
      } else {
        this.clipWindowError = ''
      }
    },
    formatSeconds(value) {
      const seconds = Math.max(0, Math.round(Number(value || 0)))
      const hours = Math.floor(seconds / 3600)
      const minutes = Math.floor((seconds % 3600) / 60)
      const remaining = seconds % 60
      return [hours, minutes, remaining]
        .map(part => String(part).padStart(2, '0'))
        .join(':')
    },
    formatJobType(value) {
      const labels = {
        ivs_recording: "IVS recording session",
        live_url: "Live URL recording",
        published_video: "Published video download (legacy)",
        stream_recording: "Stream recording",
      }
      return labels[value] || "Source input"
    },
    formatJobStatus(value) {
      const labels = {
        available: "Available",
        selected: "Selected",
        queued: "Queued",
        dispatched: "Dispatched",
        running: "Running",
        completed: "Completed",
        failed: "Failed",
      }
      return labels[value] || "Unknown"
    },
    jobStatusClass(value) {
      const base = "inline-flex items-center rounded px-2 py-1 text-xs font-medium"
      if (value === "completed") return `${base} bg-green-100 text-green-800`
      if (value === "failed") return `${base} bg-red-100 text-red-800`
      if (value === "available") return `${base} bg-yellow-100 text-yellow-800`
      if (value === "running" || value === "dispatched") return `${base} bg-blue-100 text-blue-800`
      return `${base} bg-gray-100 text-gray-800`
    },
    formatDurationMs(value) {
      const totalSeconds = Math.max(Math.floor(Number(value || 0) / 1000), 0)
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60
      if (hours) return `${hours}h ${minutes}m ${seconds}s`
      if (minutes) return `${minutes}m ${seconds}s`
      return `${seconds}s`
    },
    formatJobDateTime(value, timeZone = this.schedule_timezone || "UTC") {
      if (!value) return ""
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return ""
      try {
        return new Intl.DateTimeFormat(undefined, {
          timeZone,
          timeZoneName: "short",
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(parsed)
      } catch (error) {
        return parsed.toLocaleString()
      }
    },
    formatJobSchedule(job) {
      const timeZone = job.schedule_timezone || this.schedule_timezone || "UTC"
      const start = this.formatJobDateTime(job.scheduled_start_at, timeZone)
      const end = this.formatJobDateTime(job.scheduled_end_at, timeZone)
      if (start && end) return `${start} to ${end}`
      if (start) return `Starts ${start}`
      if (end) return `Ends ${end}`
      return "No scheduled window"
    },
    isTerminalSourceInputJob(job) {
      return job && (job.status === "completed" || job.status === "failed" || job.status === "available")
    },
    isAvailableIvsRecordingSession(job) {
      return job && job.job_type === "ivs_recording" && job.status === "available"
    },
    hasActiveSourceInputJobs() {
      return this.source_input_jobs.some(job => !this.isTerminalSourceInputJob(job))
    },
    updateSourceInputPolling() {
      if (!this.selected_ceremony || !this.hasActiveSourceInputJobs()) {
        this.stopSourceInputPolling()
        return
      }
      if (this.sourceInputPollTimer) return
      this.sourceInputPollTimer = window.setInterval(
        this.refreshSourceInputJobs,
        15000
      )
    },
    stopSourceInputPolling() {
      if (this.sourceInputPollTimer) {
        window.clearInterval(this.sourceInputPollTimer)
        this.sourceInputPollTimer = null
      }
    },
    async refreshSourceInputJobs() {
      if (!this.selected_ceremony) {
        this.stopSourceInputPolling()
        return
      }
      if (this.sourceInputPollInFlight) return
      this.sourceInputPollInFlight = true
      try {
        const r = await axios.get(this.sourceInputUrl())
        this.source_input_jobs = Array.isArray(r.data && r.data.jobs) ? r.data.jobs : []
        const settings = r.data && r.data.settings
        if (settings && settings.input_mode === "stream") {
          this.stream_key_hint = settings.stream_key_hint || this.stream_key_hint
          this.stream_ingest_urls = settings.ingest_urls || this.stream_ingest_urls
        }
      } catch (error) {
        console.log(`Could not refresh source input jobs: ${error.message}`)
      } finally {
        this.sourceInputPollInFlight = false
        this.updateSourceInputPolling()
      }
    },

    async getSignedUrl(ceremony_id, file_name, references, type = "FILE") {
      if (this.clipWindowError) {
        throw new Error(this.clipWindowError)
      }
      if (
        (this.clipWindow.startSeconds !== null || this.clipWindow.endSeconds !== null) &&
        this.clipWindowPayload === null
      ) {
        throw new Error('Clip window must include a start and end time.')
      }
      return axios
        .post(`/ceremonies/${ceremony_id}/upload`, {
          filename: file_name,
          references: references,
          type: type,
          usages: this.source_usages,
          fileSize: this.source_file ? this.source_file.size : undefined,
          multipart: this.source_file ? this.source_file.size >= 4 * 1024 * 1024 * 1024 : false,
          clipWindow: this.clipWindowPayload,
          duplicateCheck: this.duplicateCheckPayload,
          processingProfile: this.source_usages.length === 1 && this.source_usages[0] === "digital_download"
            ? "download_only"
            : this.source_usages.includes("digital_download")
              ? "clip_and_download"
              : "clip_source"
        })
        .then((response) => response.data);
    },
    sourceInputUrl(path = "") {
      const base = (process.env.VUE_APP_SOURCE_INPUT_API_BASE_URL || "/source-input").replace(/\/$/, "")
      const route = `/universities/${this.selected_university["id"]}/ceremonies/${this.selected_ceremony}/inputs${path}`
      return `${base}${route}`
    },
    async checkForOverwrite() {
      if (this.isUploading || (this.source_mode === 'file' && this.duplicateCheckPending)) return
      // Display modal if showAll is true
      const isOverwritePotential = this.showAll
      if(isOverwritePotential){
        return this.validateCeremonyOverwrite()
      }
      // showAll is false, submit
      return this.submitForUpload()
    },
    async successOverwriteValidation(){
      // Hide modal and submit
      this.isCeremonyOverwriteModalVisible = false 
      this.submitForUpload()
    },
    async submitForUpload() {
      if (!this.canSubmitUpload || this.isUploading) return
      if (this.source_mode === "external_url") {
        return this.submitExternalSource()
      }
      if (this.source_mode === "stream") {
        return this.saveStreamSettings({ scheduleRecording: true })
      }
      if (!this.$refs.duplicateCheck) return
      const file = this.source_file
      const ceremony = this.selected_ceremony
      const payload = await this.$refs.duplicateCheck.confirmUpload()
      if (!payload || file !== this.source_file || ceremony !== this.selected_ceremony || this.isUploading) return
      this.duplicateCheckPayload = payload
      return this.submitSourceFile();
    },
    inputSettingsPayload(extra = {}) {
      return {
        inputMode: this.source_mode === "stream" ? "stream" : this.source_mode,
        streamProtocols: this.stream_protocols,
        scheduledStartAt: this.scheduleDateTimeToIso(this.scheduled_start_at),
        scheduledEndAt: this.scheduleDateTimeToIso(this.scheduled_end_at),
        scheduleTimezone: this.schedule_timezone || "UTC",
        reconnectGraceSeconds: this.reconnect_grace_seconds,
        stallTimeoutSeconds: this.stall_timeout_seconds,
        sourceUrl: this.source_mode === "stream" ? null : (this.external_source_url || null),
        sourceUrlType: this.source_mode === "stream" ? "stream_recording" : "live_url",
        usages: this.source_usages,
        processingProfile: this.source_usages.length === 1 && this.source_usages[0] === "digital_download"
          ? "download_only"
          : this.source_usages.includes("digital_download")
            ? "clip_and_download"
            : "clip_source",
        ...extra,
      }
    },
    resetSourceInputState() {
      this.stopSourceInputPolling()
      this.source_mode = "file"
      this.external_source_url = ""
      this.external_job_type = "live_url"
      this.scheduled_start_at = ""
      this.scheduled_end_at = ""
      this.schedule_timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
      this.stream_protocols = ["rtmp", "srt"]
      this.stream_ingest_urls = {}
      this.reconnect_grace_seconds = 300
      this.stall_timeout_seconds = 45
      this.stream_key_hint = ""
      this.new_stream_key = ""
      this.source_input_jobs = []
      this.current_input_settings = null
      this.completed = false
    },
    scheduleDateTimeToIso(value) {
      if (!value) return null
      const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/)
      if (!match) return null
      const [, year, month, day, hour, minute] = match.map(Number)
      const utcGuess = Date.UTC(year, month - 1, day, hour, minute)
      let offsetMs = this.timeZoneOffsetMs(utcGuess, this.schedule_timezone || "UTC")
      let instantMs = utcGuess - offsetMs
      const correctedOffsetMs = this.timeZoneOffsetMs(instantMs, this.schedule_timezone || "UTC")
      if (correctedOffsetMs !== offsetMs) {
        instantMs = utcGuess - correctedOffsetMs
      }
      const parsed = new Date(instantMs)
      if (Number.isNaN(parsed.getTime())) return null
      return parsed.toISOString()
    },
    timeZoneOffsetMs(timestampMs, timeZone) {
      try {
        const parts = new Intl.DateTimeFormat("en-GB", {
          timeZone,
          hourCycle: "h23",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }).formatToParts(new Date(timestampMs))
        const values = Object.fromEntries(parts.filter(part => part.type !== "literal").map(part => [part.type, part.value]))
        const asUtc = Date.UTC(
          Number(values.year),
          Number(values.month) - 1,
          Number(values.day),
          Number(values.hour),
          Number(values.minute),
          Number(values.second || 0)
        )
        return asUtc - timestampMs
      } catch (error) {
        return 0
      }
    },
    isoToScheduleDateTime(value, timeZone = this.schedule_timezone || "UTC") {
      if (!value) return ""
      const parsed = new Date(value)
      if (Number.isNaN(parsed.getTime())) return ""
      try {
        const parts = new Intl.DateTimeFormat("en-GB", {
          timeZone,
          hourCycle: "h23",
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
          hour: "2-digit",
          minute: "2-digit",
        }).formatToParts(parsed)
        const values = Object.fromEntries(parts.filter(part => part.type !== "literal").map(part => [part.type, part.value]))
        return `${values.year}-${values.month}-${values.day}T${values.hour}:${values.minute}`
      } catch (error) {
        const offsetMs = parsed.getTimezoneOffset() * 60 * 1000
        return new Date(parsed.getTime() - offsetMs).toISOString().slice(0, 16)
      }
    },
    async loadInputSettings() {
      if (!this.selected_ceremony || !this.canManageSourceInputs) return
      try {
        const r = await axios.get(this.sourceInputUrl())
        const settings = r.data && r.data.settings
        this.current_input_settings = settings || null
        this.source_input_jobs = Array.isArray(r.data && r.data.jobs) ? r.data.jobs : []
        if (!settings) {
          this.updateSourceInputPolling()
          return
        }
        this.schedule_timezone = settings.schedule_timezone || this.schedule_timezone || "UTC"
        this.scheduled_start_at = this.isoToScheduleDateTime(settings.scheduled_start_at, this.schedule_timezone)
        this.scheduled_end_at = this.isoToScheduleDateTime(settings.scheduled_end_at, this.schedule_timezone)
        this.stream_protocols = Array.isArray(settings.stream_protocols) && settings.stream_protocols.length
          ? settings.stream_protocols
          : ["rtmp", "srt"]
        this.reconnect_grace_seconds = settings.reconnect_grace_seconds || 300
        this.stall_timeout_seconds = settings.stall_timeout_seconds || 45
        this.stream_key_hint = settings.stream_key_hint || ""
        if (settings.input_mode === "stream") {
          this.source_mode = "stream"
          this.stream_ingest_urls = settings.ingest_urls || {}
        } else if (settings.input_mode === "external_url") {
          this.source_mode = "external_url"
          this.external_source_url = settings.source_url || this.external_source_url
          this.external_job_type = "live_url"
        }
        this.updateSourceInputPolling()
      } catch (error) {
        console.log(`Could not load input settings: ${error.message}`)
      }
    },
    async saveStreamSettings(extra = {}) {
      this.isUploading = true
      this.uploadStatus = 'Saving stream settings...'
      try {
        const r = await axios.put(
          this.sourceInputUrl(),
          this.inputSettingsPayload(extra)
        )
        const settings = r.data && r.data.settings
        if (settings) {
          this.current_input_settings = settings
          this.schedule_timezone = settings.schedule_timezone || this.schedule_timezone || "UTC"
          this.stream_key_hint = settings.stream_key_hint || ""
          this.new_stream_key = settings.stream_key || ""
          this.stream_ingest_urls = settings.ingest_urls || this.stream_ingest_urls
        }
        if (r.data && r.data.job) {
          this.source_input_jobs = [r.data.job, ...this.source_input_jobs.filter(job => job.id !== r.data.job.id)].slice(0, 10)
        }
        this.updateSourceInputPolling()
        this.completed = false
        this.uploadStatus = this.new_stream_key
          ? 'Stream ingest created. Copy the stream key now.'
          : 'Stream settings saved.'
      } catch (error) {
        this.fileUploadMessage = error.response?.data?.error || error.message || 'Stream settings could not be saved.'
        this.isFileUploadModalVisible = true
        this.uploadStatus = ''
      } finally {
        this.isUploading = false
      }
    },
    async rotateStreamKey() {
      await this.saveStreamSettings({ rotateStreamKey: true })
    },
    async selectIvsRecordingSession(job) {
      if (!job || !job.id) return
      this.isUploading = true
      this.uploadStatus = 'Queuing IVS recording import...'
      try {
        const r = await axios.post(this.sourceInputUrl(`/jobs/${job.id}/select`))
        if (r.data && r.data.job) {
          this.source_input_jobs = [r.data.job, ...this.source_input_jobs.filter(existing => existing.id !== r.data.job.id)].slice(0, 10)
        }
        this.updateSourceInputPolling()
        this.uploadStatus = 'IVS recording selected. Import is queued.'
      } catch (error) {
        this.fileUploadMessage = error.response?.data?.error || error.message || 'The IVS recording could not be selected.'
        this.isFileUploadModalVisible = true
        this.uploadStatus = ''
      } finally {
        this.isUploading = false
      }
    },
    async submitExternalSource() {
      this.isUploading = true
      this.uploadStatus = 'Creating ingestion job...'
      try {
        const r = await axios.post(
          this.sourceInputUrl("/jobs"),
          {
            sourceUrl: this.external_source_url,
            jobType: "live_url",
            scheduledStartAt: this.scheduleDateTimeToIso(this.scheduled_start_at),
            scheduledEndAt: this.scheduleDateTimeToIso(this.scheduled_end_at),
            scheduleTimezone: this.schedule_timezone || "UTC",
            reconnectGraceSeconds: this.reconnect_grace_seconds,
            stallTimeoutSeconds: this.stall_timeout_seconds,
            usages: this.source_usages,
            processingProfile: this.source_usages.length === 1 && this.source_usages[0] === "digital_download"
              ? "download_only"
              : this.source_usages.includes("digital_download")
                ? "clip_and_download"
                : "clip_source",
          }
        )
        if (r.data && r.data.job) {
          this.source_input_jobs = [r.data.job, ...this.source_input_jobs.filter(job => job.id !== r.data.job.id)].slice(0, 10)
        }
        this.updateSourceInputPolling()
        this.completed = true
        this.uploadStatus = ''
      } catch (error) {
        this.fileUploadMessage = error.response?.data?.error || error.message || 'The ingestion job could not be created.'
        this.isFileUploadModalVisible = true
        this.uploadStatus = ''
      } finally {
        this.isUploading = false
      }
    },
    async submitSourceFile() {
      this.stage = 100;
      this.isUploading = true;
      this.uploadStatus = 'Preparing upload...'
      try {
        var signedUrl = await this.getSignedUrl(
          this.selected_ceremony,
          this.source_file.name,
          this.references
        );
        signedUrl = signedUrl.url
        this.progress = 0;

        if (signedUrl.uploadMethod === "multipart") {
          await this.uploadMultipartSourceFile(signedUrl)
          this.completed = true
          this.uploadStatus = ''
          this.trackSourceUploadCompleted()
          return
        }

        var formData = new FormData();
        for (const [key, value] of Object.entries(signedUrl.fields)) {
          formData.append(key, value);
        }
        var file = new File([this.source_file], this.source_file.name);
        formData.append("file", file);
        await axios.post(signedUrl.url, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          transformRequest: (data, headers) => {
            delete headers.Authorization;
            delete headers.authorization;
            if (headers.common) {
              headers.common = { ...headers.common };
              delete headers.common["Authorization"];
              delete headers.common.authorization;
            }
            return data;
          },
          onUploadProgress: (progressEvent) => {
            this.stage = -1;
            let percentCompleted = Math.floor(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            // do whatever you like with the percentage complete
            // maybe dispatch an action that will update a progress bar or something
            this.progress = percentCompleted;
          },
        })
        this.completed = true;
        this.uploadStatus = ''
        this.trackSourceUploadCompleted()
      } catch (error) {
        console.log(`Error uploading file: ${error.message}`);
        if (this.$refs.duplicateCheck?.handleUploadError(error)) {
          this.stage = 1
          this.isUploading = false
          this.uploadStatus = ''
          return
        }
        this.fileUploadMessage = error.message || 'The upload failed. Please try again.'
        this.setSourceFileToNull = false
        this.isFileUploadModalVisible = true
        this.isUploading = false
        this.uploadStatus = ''
      }
    },
    trackSourceUploadCompleted() {
      trackAdminEvent('admin_action_completed', {
        toolKey: 'clip_upload',
        metadata: {
          action: 'source_upload_complete',
          ceremonyId: this.selected_ceremony,
          universityId: this.selected_university && this.selected_university.id,
          usages: this.source_usages,
          fileSize: this.source_file && this.source_file.size,
        },
      })
    },
    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    partRetryDelay(attempt) {
      const baseDelay = PART_UPLOAD_RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1);
      const jitter = Math.floor(Math.random() * 250);
      return baseDelay + jitter;
    },
    updateMultipartProgress(partProgress) {
      const loaded = Object.values(partProgress).reduce((total, value) => total + value, 0);
      this.progress = Math.min(99, Math.floor((loaded * 100) / this.source_file.size));
    },
    normalizeETag(etag) {
      if (!etag) return etag;
      return Array.isArray(etag) ? etag[0] : etag;
    },
    async uploadMultipartPart(part, signedUrl, partProgress) {
      const file = this.source_file;
      const start = (part.partNumber - 1) * signedUrl.partSize;
      const end = Math.min(start + signedUrl.partSize, file.size);
      const blob = file.slice(start, end);
      let lastError = null;

      for (let attempt = 1; attempt <= MAX_PART_UPLOAD_ATTEMPTS; attempt += 1) {
        try {
          this.uploadStatus = `Uploading part ${part.partNumber} of ${signedUrl.parts.length}`
          if (attempt > 1) {
            this.uploadStatus = `Retrying part ${part.partNumber} of ${signedUrl.parts.length} (${attempt} of ${MAX_PART_UPLOAD_ATTEMPTS})`
          }
          partProgress[part.partNumber] = 0;
          this.updateMultipartProgress(partProgress);
          const result = await axios.put(part.url, blob, {
            headers: {
              "Content-Type": "video/mp4",
            },
            transformRequest: (data, headers) => {
              delete headers.common["Authorization"];
              return data;
            },
            onUploadProgress: (progressEvent) => {
              this.stage = -1;
              partProgress[part.partNumber] = progressEvent.loaded;
              this.updateMultipartProgress(partProgress);
            },
          });
          partProgress[part.partNumber] = blob.size;
          this.updateMultipartProgress(partProgress);
          return {
            ETag: this.normalizeETag(result.headers.etag),
            PartNumber: part.partNumber,
          };
        } catch (error) {
          lastError = error;
          partProgress[part.partNumber] = 0;
          this.updateMultipartProgress(partProgress);
          if (attempt < MAX_PART_UPLOAD_ATTEMPTS) {
            await this.sleep(this.partRetryDelay(attempt));
          }
        }
      }

      throw new Error(
        `Part ${part.partNumber} failed after ${MAX_PART_UPLOAD_ATTEMPTS} attempts: ${lastError && lastError.message ? lastError.message : 'upload failed'}`
      );
    },
    async uploadMultipartSourceFile(signedUrl) {
      const parts = [];
      const partProgress = {};
      let nextPartIndex = 0;

      const uploadNextPart = async () => {
        while (nextPartIndex < signedUrl.parts.length) {
          const part = signedUrl.parts[nextPartIndex];
          nextPartIndex += 1;
          const uploadedPart = await this.uploadMultipartPart(part, signedUrl, partProgress);
          parts.push(uploadedPart);
        }
      };

      const workerCount = Math.min(MAX_PARALLEL_PART_UPLOADS, signedUrl.parts.length);
      await Promise.all(Array.from({ length: workerCount }, uploadNextPart));

      const completedParts = parts.sort((a, b) => a.PartNumber - b.PartNumber);
      if (completedParts.length !== signedUrl.parts.length) {
        throw new Error('Not all upload parts completed. Please try again.');
      }

      this.uploadStatus = 'Completing upload...'
      await axios.post(`/ceremonies/${this.selected_ceremony}/upload`, {
        action: "complete_multipart_upload",
        key: signedUrl.key,
        sourceUploadId: signedUrl.uploadId ? (signedUrl.sourceUploadId || null) : null,
        uploadId: signedUrl.uploadId,
        parts: completedParts,
      });
      this.progress = 100;
      this.uploadStatus = ''
    },

    onUniChange() {
      this.setSourceFile(null)
      this.resetCeremonySkim()
      this.getCeremonies(this.selected_university["id"]).then(
        (ceremonies) => (this.ceremonies = ceremonies)
      );
      this.stage = 1;
      this.selected_ceremony = null;
      this.resetSourceInputState()
    },
    onCeremonyChange() {
      this.setSourceFile(null)
      this.resetCeremonySkim()
      this.resetSourceInputState()
      if (this.canManageSourceInputs) this.loadInputSettings()
    },
    async getCeremonies(uni_id) {
      if (typeof this.showAll != "undefined" && this.showAll) {
        return axios
          .get(`/universities/${uni_id}/ceremonies?scope=upload`)
          .then((response) => response.data);
      } else {
        return axios
          .get(`/universities/${uni_id}/ceremonies?ready=false&scope=upload`)
          .then((response) => response.data);
      }
    },
    async applyRouteSelection() {
      const universityId = this.$route.query.universityId
      const ceremonyId = this.$route.query.ceremonyId
      const sourceMode = this.$route.query.sourceMode
      if (!universityId) return
      const university = this.universities.find(item => item.id === universityId)
      if (!university) return
      this.selected_university = university
      this.stage = 1
      if (ceremonyId) this.showAll = true
      this.ceremonies = await this.getCeremonies(universityId)
      if (ceremonyId && this.ceremonies.some(item => item.id === ceremonyId)) {
        this.selected_ceremony = ceremonyId
        if (this.canManageSourceInputs) await this.loadInputSettings()
        if (sourceMode === "file" || (this.canManageSourceInputs && ["external_url", "stream"].includes(sourceMode))) {
          this.setSourceMode(sourceMode)
        }
      }
    },
    async onFileChange(e) {
      var files = e.target.files || e.dataTransfer.files;
      this.resetCeremonySkim()
      if (!files.length) return;
      this.source_file = files[0];
      this.references = { wide: null, close: null }
      this.duplicateCheckPayload = null
      this.clipWindow = {
        startSeconds: null,
        endSeconds: null,
      }
      this.clipWindowError = ''
      const valid = await this.validateFileUpload(this.source_file)
      if (valid) this.runCeremonySkim(this.source_file)
    },
    async validateFileUpload(file) {
      try {
        this.validateFileSize(file);
        // File passed validation checks
        return true;
      } catch (error) {
        console.error(`Error validating file: ${error.message}`);
        this.fileUploadMessage = error.message;
        this.isFileUploadModalVisible = true;
        return false;
      }
    },
    validateFileSize(file){
      const maxSizeInBytes = 6 * 1024 * 1024 * 1024;
      console.log(`File size: ${(file.size / (1000 * 1000 * 1000)).toFixed(2)} GB`);
      if (file.size > maxSizeInBytes) {
        this.setSourceFileToNull = true;
        throw new Error('This file is over the current 6GB source upload limit. Please upload a smaller source file.')
      }
      return true;
    },
    setSourceFile(v){
      if (!v) this.resetCeremonySkim()
      this.source_file = v;
      this.duplicateCheckPayload = null
      this.references = { wide: null, close: null }
      this.clipWindow = { startSeconds: null, endSeconds: null }
      this.clipWindowError = ''
      if (this.$refs.file) this.$refs.file.value = '';
    },
    replaceSourceFile() {
      this.setSourceFile(null)
      this.$nextTick(() => {
        this.$refs.file?.focus()
        this.$refs.file?.click()
      })
    },
    acceptError(sourceFileToNull) {
      this.isFileUploadModalVisible = false;
      if (sourceFileToNull == true)
        this.setSourceFile(null);
    },
    formatCeremonyOption(ceremony) {
      if (!ceremony) return "";
      const formattedDate = this.formatDate(ceremony.datetime);
      return ceremony.label
        ? `${formattedDate} - ${ceremony.label}`
        : formattedDate;
    },
    formatDate(dateString) {
      if (!dateString) return "";
      return new Date(dateString).toLocaleDateString("en-GB", {
        weekday: "long",
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      });
    },
  },

  components: { CeremonySkimAssist, RecordingDuplicateCheck },
};
</script>
<style scoped>
.upload-video-player {
  width: 100% !important;
  max-width: 100%;
}
button#overwriteModalSubmitBtn:disabled{
  visibility: visible!important;
  background-color: grey;
  color: white;
  opacity: 0.5;
  cursor: none;
}
</style>
